import React, { useRef } from "react";
import img from "../../../constant";
import { X } from "lucide-react";

interface MSAContentProps {
  clientName: string;
  representativeName: string;
  representativeRole: string;
  date: string;
  effectiveDate: string;
  uploadedSignature?: File | null;
  signaturePreview?: string | null;
  onFileUpload?: (file: File) => void;
  onRemoveSignature?: () => void;
  readOnly?: boolean;
}

const MSAContent: React.FC<MSAContentProps> = ({
  clientName,
  date,
  effectiveDate,
  representativeName,
  representativeRole,
  uploadedSignature,
  signaturePreview,
  onFileUpload,
  onRemoveSignature,
  readOnly = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const validTypes = ["image/jpeg", "image/jpg", "image/png"];
      if (!validTypes.includes(file.type)) {
        alert("Please upload a valid image file (JPG, JPEG, or PNG)");
        return;
      }
      onFileUpload?.(file);
    }
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRemoveSignature?.();
  };

  return (
    <div className="font-abel text-gray-700 text-sm leading-relaxed space-y-6 p-5 bg-white shadow-sm rounded-lg border border-gray-100 max-w-[1024px] mx-auto">
      {/* Header */}
      <div className="text-center mb-10 border-b pb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Master Services Agreement
        </h1>
        <h2 className="text-xl font-semibold text-gray-600 mb-2">(MSA)</h2>
        <p className="text-base mt-4">
          This Master Services Agreement (&quot;Agreement&quot;) is entered into as of the date last signed below {" "}
          <span className="font-semibold text-primary">{effectiveDate}</span> by
          and between:
        </p>
        <div className="mt-4 space-y-2 text-left max-w-md mx-auto bg-gray-50 rounded-lg p-4 text-sm">
          <p>
            25th &amp; Staffing Limited
          </p>
          {/* <p className="text-gray-500 text-xs"> */}
            <i className="text-gray-500 text-xs mb-0">trading as 25th &amp; Staffing, with its principal place of business at:</i> 
          {/* </p> */}
          <p className="text-gray-500 text-xs">
            Architect Place, 2 Idowu Taylor Street, Victoria Island, Lagos
            and the entity identified in the signature block below 
          </p>
          <p className="mt-2">
            <strong>Customer:</strong>{" "}
            <span className="text-primary font-bold">{clientName}</span>
          </p>
        </div>

        <p className="text-sm text-gray-500 mt-4">
          Provider and Customer may be referred to individually as a
          &quot;Party&quot; and collectively as the &quot;Parties.&quot;
          This Agreement governs Customer's access to and use of the 25th Suite platform and related services.
        </p>
      </div>

      <div className="bg-gray-50 p-6 rounded-md mb-8">
        <h3 className="font-bold text-lg mb-4">Table of Contents</h3>
        <ul className="list-decimal pl-5 space-y-1">
          <li>Services</li>
          <li>Fees and Payment</li>
          <li>Term and Termination</li>
          <li>Intellectual Property</li>
          <li>Confidentiality</li>
          <li>Data and Security</li>
          <li>Warranties and Disclaimers</li>
          <li>Indemnification</li>
          <li>Limitation of Liability</li>
          <li>Force Majeure</li>
          <li>General Provisions</li>
          <li>Professional Services & Security Engagements</li>
          <li>Execution</li>
        </ul>
      </div>

      {/* Section 1 */}
      <section>
        <h3 className="font-bold text-lg mb-3 text-gray-900 border-b border-gray-200 pb-2">
          1. Services
        </h3>
        <p className="mb-2">
          Provider agrees to provide Customer with access to its proprietary
          platform known as the <strong>25th Suite</strong> (“Services”),
          comprising:
        </p>

        <ul className="list-none ml-4 mb-3 space-y-1">
          <li>
            (a) <strong>AI Recruiter</strong>
          </li>
          <li>
            (b) <strong>25th Direct</strong>
          </li>
          <li>
            (c) <strong>25th Console</strong>
          </li>
          <li>
            (d) <strong>Employee Lifecycle</strong>
          </li>
        </ul>

        <p className="mb-2">
          Provider shall also deliver any related implementation, training, or
          support services (“Professional Services”) as detailed in one or more
          executed Subscription Orders or Statements of Work (SOWs).
        </p>

        <p className="mt-2">
          All Subscription Orders and SOWs shall be governed by the terms of
          this Agreement.
        </p>
      </section>

      {/* Section 2 */}
      <section>
        <h3 className="font-bold text-lg mb-3 text-gray-900 border-b border-gray-200 pb-2">
          2. Fees and Payment
        </h3>
        <div className="space-y-3">
          <div>
            <h4 className="font-semibold mb-1">2.1 Fees</h4>
            <p>
              Customer shall pay the fees stated in each subscription order or
              as published on provider&apos;s website at the time of purchase.
              All fees are non-refundable except as expressly provided in this
              agreement or the Service Level Agreement (SLA).
            </p>
          </div>
          <div>
            <h4 className="font-semibold mb-1">2.2 Invoicing</h4>
            <p>
              Fees will be invoiced in Nigerian Naira (NGN) or such other currency as agreed
              in the applicable Subscription Order. Unless otherwise specified:
            </p>
            <ul className="list-disc pl-5 mt-1 space-y-1">
              <li>
              	Subscription fees are invoiced annually in advance. 
                Quarterly billing is available at a 15% premium on the annual equivalent, 
                invoiced at the start of each quarter. Monthly billing is not available as a standard option 
                and may only be offered pursuant to a separate written addendum signed by both Parties
              </li>
              <li>
              	One-time setup or activation fees are invoiced upon execution of the applicable Subscription Order
              </li>
            </ul>
          </div>
          <div>
            <h4 className="font-semibold mb-1">2.2.1 VAT</h4>
            <p>
              All fees are exclusive of Value Added Tax (VAT). VAT at the
              prevailing statutory rate shall be added to all taxable services
              and invoiced accordingly. Customer is responsible for any applicable withholding taxes, 
              provided that Customer shall gross up any payment subject to withholding so that 
              Provider receives the full invoiced amount.
            </p>
          </div>
          <div>
            <h4 className="font-semibold mb-1">2.3 Payment Terms</h4>
            <p>
              Payment shall be made as follows: 70% upon execution of the Subscription Order 
              or receipt of invoice (whichever is earlier), with the remaining 
              30% due upon go-live as defined below.
            </p>
          </div>
          <div>
            <h4 className="font-semibold mb-1">Definition of Go-Live:</h4>
            <p>
              "Go-live" means the date on which Customer's designated representative confirms 
              in writing that the Services are operational, or 5 business days after 
              Provider delivers written notice of readiness for go-live, whichever is earlier. 
              Customer shall not unreasonably withhold or delay such written confirmation.
            </p>
          </div>
          <div>
            <h4 className="font-semibold mb-1">2.4 Non-Payment Remedies</h4>
            <p>
              If any undisputed invoice is not paid within 30 days of the due
              date, Provider may, upon written notice:
            </p>
            <ul className="list-disc pl-5 mt-1 space-y-1">
              <li>
                Charge late interest at 1.5% per month (or the maximum rate
                permitted by law, if lower) on the outstanding balance, 
                accruing daily from the due date until the date of payment in full
              </li>
              <li>
              	Suspend Customer's access to the Services until full payment (including accrued interest) is received
              </li>
              <li>
                Terminate this Agreement for material breach if non-payment continues for 
                an additional 15 days following written notice of suspension 
              </li>
            </ul>
            <p className="mt-2 text-xs text-gray-600">
              Non-payment of any milestone-based balance after the relevant milestone has been 
              achieved and accepted, or deemed accepted per Section 2.3,
              shall entitle Provider to immediately suspend or terminate access 
              without further notice, without refund, and without liability for termination fees.
            </p>
          </div>
          <div>
            <h4 className="font-semibold mb-1">2.5 AI Credit Pricing Adjustment</h4>
            <p>
              Certain Services include AI-powered features billed on consumption of AI credits, 
              which are dependent on third-party AI infrastructure providers. 
              In the event that Provider's underlying third-party AI vendor increases its pricing,
              Provider reserves the right to pass through such increases to Customer by adjusting 
              the price of AI credits accordingly. Provider shall use commercially reasonable efforts
              to notify Customer at least 30 days in advance of any such adjustment. 
              Customer's continued use of the affected Services following notification 
              shall constitute acceptance of the revised pricing.
            </p>
          </div>
          <div>
            <h4 className="font-semibold mb-1">2.6 Service Credits</h4>
            <p>
              Where Provider fails to meet the uptime commitments set out in the Service Level Agreement (Exhibit B),
              Customer shall be entitled to service credits calculated on the monthly fee equivalent of the affected 
              subscription, as follows:
            </p>

            <div className="overflow-x-auto mt-2">
            <table className="min-w-full text-left text-xs border">
              <thead className="bg-gray-100 font-bold">
                <tr>
                  <th className="p-2 border">Uptime Achieved</th>
                  <th className="p-2 border">Credit</th>
                  <th className="p-2 border">Conditions</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="p-2 border font-semibold">
                    Below SLA but above 99.0%
                  </td>
                  <td className="p-2 border">
                    10% of that month's fee
                  </td>
                  <td className="p-2 border font-semibold">
                    Claimed within 30 days of incident
                  </td>

                </tr>
                <tr>
                  <td className="p-2 border font-semibold">
                    95.0% – 99.0%
                  </td>
                  <td className="p-2 border">
                    20% of that month's fee
                  </td>
                  <td className="p-2 border font-semibold">
                    Claimed within 30 days of incident
                  </td>
                 
                </tr>
                <tr>
                  <td className="p-2 border font-semibold">
                    Below 95.0%
                  </td>
                  <td className="p-2 border">
                    40% of that month's fee
                  </td>
                  <td className="p-2 border font-semibold">
                    Claimed within 30 days; termination right triggered if sustained for 2 consecutive months
                  </td>
                  
                </tr>
              </tbody>
            </table>
          </div>
          
          <p className="mt-3">Service credits are capped at one month's fee per calendar quarter. 
            Credits constitute Customer's sole and exclusive remedy for SLA breaches 
            and shall not entitle Customer to terminate this Agreement except where 
            uptime falls below 95.0% for two consecutive calendar months. Credits do 
            not apply to downtime caused by: (i) scheduled maintenance notified at least 
            48 hours in advance; (ii) Force Majeure Events; (iii) acts or omissions of Customer 
            or its Authorised Users; or (iv) third-party service failures outside Provider's 
            reasonable control.</p>

        </div>
        </div>
      </section>

      {/* Section 3 */}
      <section>
        <h3 className="font-bold text-lg mb-3 text-gray-900 border-b border-gray-200 pb-2">
          3. Term and Termination
        </h3>
        <div className="space-y-3">
          <div>
            <h4 className="font-semibold mb-1">3.1 Term</h4>
            <p>
              This Agreement commences on the Effective Date and continues until terminated in accordance with its terms.
            </p>
          </div>
          <div>
            <h4 className="font-semibold mb-1">
              3.2 Subscription Term and Renewals
            </h4>
            <p>
              The initial subscription term shall be as stated in the applicable Subscription Order.
              Each subscription shall automatically renew for successive terms of equal length unless 
              either Party provides the other with written notice of non-renewal at least 60 days prior to 
              the end of the then-current term. Provider reserves the right to adjust pricing upon renewal,
              subject to 60 days' prior written notice.
            </p>
          </div>
          <div>
            <h4 className="font-semibold mb-1">3.3 Termination for Cause</h4>
            <p>
              Either Party may terminate this Agreement for material breach upon 30 days' written notice,
              provided the breaching Party fails to cure such breach within that notice period. 
              Upon termination for any reason: (a) Customer's access to the Services shall immediately cease; 
              (b) all fees accrued prior to the date of termination shall remain due and payable; and 
              (c) Provider shall make Customer Data available for export for a period of 30 days following termination, 
              after which it shall be deleted in accordance with the Data Retention Schedule (Annex 3 to the DPA).
            </p>
          </div>
        </div>
      </section>

      {/* Section 4 */}
      <section>
        <h3 className="font-bold text-lg mb-3 text-gray-900 border-b border-gray-200 pb-2">
          4. Intellectual Property
        </h3>
        <div className="space-y-3">
          <div>
            <h4 className="font-semibold mb-1">4.1 Provider IP Ownership</h4>
            <p>
              As between the Parties, Provider retains all right, title, and
              interest in and to the Provider IP, including all intellectual
              property rights therein. “Provider IP” means: (a) the Services
              (including the 25th Suite platform, software, source code, object
              code, algorithms, designs, architecture, templates, workflows,
              dashboards, and documentation); (b) any updates, enhancements,
              modifications, or derivatives thereof; (c) Provider’s pre-existing
              materials, tools, methodologies, and know-how; and (d) any
              intellectual property developed or acquired by Provider
              independently of this Agreement. Nothing in this Agreement
              transfers ownership of Provider IP to Customer.
            </p>
          </div>
          <div>
            <h4 className="font-semibold mb-1">
              4.2 License Grant to Customer
            </h4>
            <p>
              Subject to Customer’s full compliance with this Agreement
              (including payment of all fees), Provider hereby grants Customer a
              limited, non-exclusive, non-transferable (except as expressly
              permitted herein), non-sublicensable (except to Authorized Users
              for internal use), revocable license during the Term to: (a)
              access and use the Services solely for Customer’s internal
              business purposes in accordance with the applicable documentation
              and this Agreement; (b) permit Authorized Users to access and use
              the Services for such purposes; and (c) make a reasonable number
              of copies of the Documentation solely for internal reference in
              connection with permitted use.
            </p>
          </div>

          <div>
            <h4 className="font-semibold mb-1">4.3 License Restrictions</h4>
            <p>
              Customer shall not, and shall not permit any third party to: (a)
              reverse, decompile, disassemble, or attempt to derive source code
              from the services; (b) modify, adapt, translate, create derivative
              works of, or remove proprietary notices from the services or
              documentation; (c) sublicense, sell, lease, rent, loan,
              distribute, or commercially exploit the services except as
              expressly authorized; (d) use the services in violation of
              applicable laws or to infringe third-party rights; (e) circumvent
              or disable any security, usage limit or access controls; or (f)
              use the services beyond the scope of the license granted therein.
            </p>
          </div>

          <div>
            <h4 className="font-semibold mb-1">4.4 Feedback</h4>
            <p>
              If customer provide suggestions, comments or feedback regarding
              the services (“feedback”), Customer grants Provider a perpetual
              irrevocable, worldwide, royalty-free, transferable license to use,
              copy, modify, create derivative works of, and otherwise exploit
              such feedback for any purpose, without attribution or
              compensation.
            </p>
          </div>

          <div>
            <h4 className="font-semibold mb-1">4.5 Reservation of Rights</h4>
            <p>
              All rights not expressly granted in this Agreement are reserved by
              Provider. No implied licenses arise by estoppel, implication, or
              otherwise.
            </p>
          </div>

          <div>
            <h4 className="font-semibold mb-1">
              4.6 Customer Data (Ownership)
            </h4>
            <p>
              Customer retains all right, title, and interest in and to Customer
              Data, including all intellectual property rights therein.
              Provider's right to use Customer Data are limited to those
              expressly set out in this Agreement and the Data Processing
              Addendum.
            </p>
          </div>

          <div>
            <h4 className="font-semibold mb-1">
              4.7 User Access and Usage Limits
            </h4>
            <p className="mb-2">
              Access to the Services is limited to the number of Authorized
              Users, subscriptions, seats, modules, and usage volumes expressly
              specified in the applicable Subscription Order. Customer shall
              ensure that:
            </p>
            <ul className="list-none space-y-1">
              <li>
                (a) each Authorized User is assigned a unique login credential;
              </li>
              <li>
                (b) user accounts are not shared, transferred, or used by more
                than one individual;
              </li>
              <li>
                (c) access is used solely for Customer’s internal business
                purposes; and
              </li>
              <li>
                (d) use of the Services does not exceed purchased quantities,
                feature tiers, storage limits, API limits, or any other usage
                parameters stated in the Subscription Order or documentation.
              </li>
            </ul>
            <p className="mt-2">
              Provider may monitor usage to verify compliance. If Customer
              exceeds permitted usage, Provider may:
            </p>
            <ul className="list-none mt-1 space-y-1">
              <li>
                (i) invoice Customer for excess usage at applicable rates;
              </li>
              <li>(ii) require Customer to upgrade its subscription; or</li>
              <li>
                (iii) suspend access to affected accounts until compliance is
                restored.
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-1">
              4.8 Subsidiaries & Additional Entities
            </h4>
            <p>
              The initial AI Credits and integration are provided under the
              Parent Company agreement. Each subsidiary or sub-company
              (operating under its own legal name or brand) will require
              separate integration and configuration. We offer a 15–20% group
              discount on both integration fees and additional AI Credits. The
              discount may be adjusted upward depending on the number of
              subsidiaries onboarded under the group agreement.
            </p>
          </div>

          <div className="overflow-x-auto mt-2">
            <table className="min-w-full text-left text-xs border">
              <thead className="bg-gray-100 font-bold">
                <tr>
                  <th className="p-2 border">Term</th>
                  <th className="p-2 border">Definition / Term</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="p-2 border font-semibold">
                    Primary Entity
                  </td>
                  <td className="p-2 border">
                    The entity signing this Agreement, responsible for the master MSA, all fees, and overall compliance.
                  </td>
                </tr>
                <tr>
                  <td className="p-2 border font-semibold">
                    Member Entity
                  </td>
                  <td className="p-2 border">
                    A subsidiary or affiliated entity operating under its own legal name or brand within 
                    the same corporate group, onboarded under the Primary Entity's agreement.
                  </td>
                </tr>
                <tr>
                  <td className="p-2 border font-semibold">
                    Member Activation Fee
                  </td>
                  <td className="p-2 border">
                    20% of the Primary Entity's applicable activation fee per Member Entity onboarded. Non-refundable.
                  </td>
                </tr>
                <tr>
                  <td className="p-2 border font-semibold">
                    Seat Pool
                  </td>
                  <td className="p-2 border">
                    All seats (Primary Entity and all Member Entities combined) are drawn from the Primary Entity's 
                    purchased seat pool. Additional seats are charged at the Primary Entity's per-seat rate.
                  </td>
                </tr>
                <tr>
                  <td className="p-2 border font-semibold">
                    Tier Limits
                  </td>
                  <td className="p-2 border">
                    Maximum Member Entities: Starter — 1; Growth — 2; Scale — 5; Enterprise — by negotiation. 
                    Additional Member Entities beyond the tier maximum are charged at the Member Activation Fee rate.
                  </td>
                </tr>
                <tr>
                  <td className="p-2 border font-semibold">
                    Governance
                  </td>
                  <td className="p-2 border">
                    All Member Entities are governed by this Agreement. No separate MSA is required per Member Entity. 
                    Primary Entity is responsible for Member Entity compliance.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>


        </div>
      </section>

      {/* Section 5 */}
      <section>
        <h3 className="font-bold text-lg mb-3 text-gray-900 border-b border-gray-200 pb-2">
          5. Confidentiality
        </h3>
        <div className="space-y-3">
          <div>
            <h4 className="font-semibold mb-1">5.1 Definition</h4>
            <p>
              &quot;Confidential Information&quot; means all non-public
              information disclosed by one party to the other in connection
              with this Agreement, in any form, including business plans, 
              financial data, technical specifications, customer lists, 
              pricing, and proprietary methodologies.
            </p>
          </div>
          <div>
            <h4 className="font-semibold mb-1">5.2 Obligations</h4>
            <p>
              Each party ( &quot;Receiving Party&quot;) shall (a) hold the other Party's 
              (&quot;Disclosing Party's&quot;) Confidential Information in strict confidence
              using at least the same degree of care it uses for its own confidential information 
              (but no less than reasonable care); (b) use Confidential Information only to perform 
              its obligations or exercise its rights under this Agreement; and (c) not disclose Confidential
              Information to any third party without the Disclosing Party's prior written consent, 
              except to employees and contractors who have a need to know and are bound by equivalent 
              confidentiality obligations.
            </p>
          </div>
          <div>
            <h4 className="font-semibold mb-1">5.3 Exceptions</h4>
            <p>
              Obligations under Section 5.2 do not apply to information that: 
              (a) is or becomes publicly available through no fault of the Receiving Party; 
              (b) was already known to the Receiving Party at the time of disclosure; 
              (c) is independently developed by the Receiving Party without use of Confidential Information; or 
              (d) is required to be disclosed by law or regulatory order, provided the Receiving Party gives prompt
              written notice to the Disclosing Party (where permitted) and cooperates with any request 
              for a protective order.
            </p>
          </div>
          <div>
            <h4 className="font-semibold mb-1">5.4 Duration</h4>
            <p>Confidentiality obligations under this Section 5 shall survive termination 
              or expiry of this Agreement for a period of five (5) years.
            </p>
          </div>
        </div>
      </section>

      {/* Section 6 */}
      <section>
        <h3 className="font-bold text-lg mb-3 text-gray-900 border-b border-gray-200 pb-2">
          6. Data and Security
        </h3>
        <div className="space-y-3">
          <div>
            <h4 className="font-semibold mb-1">6.1 Security</h4>
            <p>
              Provider shall implement and maintain appropriate technical and
              organizational security measures to protect Customer Data, as
              detailed in the Service Level Agreement (Exhibit B). and the Data Processing Addendum (Exhibit C).
            </p>
          </div>
          <div>
            <h4 className="font-semibold mb-1">6.2 Data Processing</h4>
            <p>
              All processing of personal data in connection with the Services shall be governed by the Data Processing Addendum (Exhibit C),
              which is incorporated into this Agreement by reference.
            </p>
          </div>
          <div>
            <h4 className="font-semibold mb-1">
              6.3 Prohibition on Data Misuse
            </h4>
            <p>
              Neither party, nor their employees, officers, agents, affiliates,
              or subcontractors, shall sell, rent, trade, lease, share for
              remuneration, or otherwise intentionally misuse, disclose, or
              commercially exploit the other party&apos;s confidential
              information, candidate data, employee records, or any personal
              data processed under this agreement.
            </p>
            <div className="mt-2 bg-gray-50 border border-gray-100 rounded-lg p-3">
              <p className="font-semibold text-gray-800 text-xs mb-1">
                Breach &amp; Remedies
              </p>
              <p className="text-xs text-gray-700">
                In the event of a proven breach of this clause by a party
                (including willful or intentional sale, trade, or misuse of
                data):
              </p>
              <ul className="list-disc pl-4 mt-1 space-y-1 text-xs text-gray-700">
                <li>
                  Immediate termination of this agreement for cause, without
                  notice period or liability for termination fees
                </li>
                <li>
                  Liquidated damages of NGN 25,000,000 per unauthorized
                  disclosure or misuse incident (or USD equivalent at the
                   prevailing parallel market rate on the date of breach)
                </li>
                <li>
                  Full indemnification for all direct and reasonably foreseeable losses,
                  including regulatory fines, third-party claims, remediation and notification costs,
                  and reasonable legal fees.
                </li>
              </ul>
              <p className="text-xs text-gray-700 mt-1">
                Liability for willful or intentional breach under this clause
                shall not be subject to any limitation of liability in this
                agreement and shall survive termination.
              </p>
            </div>
            <div className="mt-2 bg-gray-50 rounded-lg p-3 text-xs">
              <p className="font-semibold mb-1">Standard Liability Cap</p>
              <p>
                Save for breaches under Section 6.3, each Party's total aggregate liability 
                under this Agreement shall be capped at 100% of the total fees paid or payable by Customer 
                in the twelve (12) months immediately preceding the relevant claim.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 7 */}
      <section>
        <h3 className="font-bold text-lg mb-3 text-gray-900 border-b border-gray-200 pb-2">
          7. Warranties and Disclaimers
        </h3>
        <div className="space-y-2">
          <div>
            <h4 className="font-semibold mb-1">7.1 Provider Warranty</h4>
            <p>
              Provider warrants that the Services will perform materially in accordance with 
              the applicable documentation and the Service Level Agreement (Exhibit B). 
              Where the Services fail to meet the uptime commitments set out in the SLA, 
              Customer's sole and exclusive remedy shall be the service credits set out in Section 2.6.
            </p>
          </div>
          <div>
            <h4 className="font-semibold mb-1">7.2 Disclaimer</h4>
            <p className="uppercase text-xs font-semibold text-gray-500">
              SAVE AS EXPRESSLY PROVIDED IN SECTION 7.1, THE SERVICES ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS.
              TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, PROVIDER EXPRESSLY DISCLAIMS ALL OTHER WARRANTIES, 
              WHETHER EXPRESS OR IMPLIED, INCLUDING ANY IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, 
              TITLE, AND NON-INFRINGEMENT.
            </p>
          </div>
        </div>
      </section>

      {/* Section 8 */}
      <section>
        <h3 className="font-bold text-lg mb-3 text-gray-900 border-b border-gray-200 pb-2">
          8. Indemnification
        </h3>
        <p>
          Each Party ("Indemnifying Party") shall indemnify, defend, and hold harmless the other Party and its officers, 
          directors, employees, and agents from and against any and all third-party claims, actions, proceedings, losses,
          damages, liabilities, costs, and expenses (including reasonable legal fees) arising from or in connection with: 
          (i) the Indemnifying Party's material breach of this Agreement; (ii) the Indemnifying Party's gross negligence or wilful misconduct; 
          or (iii) the Indemnifying Party's violation of applicable law.
        </p>
      </section>

      {/* Section 9 */}
      <section>
        <h3 className="font-bold text-lg mb-3 text-gray-900 border-b border-gray-200 pb-2">
          9. Limitation of Liability
        </h3>
        <p className="uppercase text-xs text-gray-500 font-semibold mb-5">
          TO THE FULLEST EXTENT PERMITTED BY LAW, IN NO EVENT SHALL EITHER PARTY&apos;S AGGREGATE LIABILITY
          TO THE OTHER ARISING OUT OF OR IN CONNECTION WITH THIS AGREEMENT EXCEED THE TOTAL FEES PAID OR 
          PAYABLE BY CUSTOMER TO PROVIDER DURING THE TWELVE (12) CALENDAR MONTHS IMMEDIATELY PRECEDING 
          THE CLAIM GIVING RISE TO LIABILITY.
        </p>
        <p className="uppercase text-xs text-gray-500 font-semibold">
          IN NO EVENT SHALL EITHER PARTY BE LIABLE TO THE OTHER FOR ANY LOSS OF PROFITS, LOSS OF REVENUE, 
          LOSS OF GOODWILL, LOSS OF DATA, OR ANY INDIRECT, SPECIAL, INCIDENTAL, PUNITIVE, OR CONSEQUENTIAL DAMAGES,
          HOWEVER ARISING, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
        </p>
        <p className="mt-2 text-xs text-gray-500">
          The foregoing limitations are subject to the expressly uncapped liability under Section 6.3 (Data Misuse)
          and Section 12.8 (Security Engagements), both of which survive termination and are not limited by this Section 9.
        </p>
      </section>

      {/* Section 10 */}
      <section>
        <h3 className="font-bold text-lg mb-3 text-gray-900 border-b border-gray-200 pb-2">
          10. Force Majeure
        </h3>
        <p>
          Neither Party shall be in breach of this Agreement or liable for delay or failure to perform any of its obligations
           to the extent that such delay or failure is caused by a Force Majeure Event. "Force Majeure Event" means any event 
           beyond a Party's reasonable control, including acts of God, natural disasters, epidemic or pandemic, acts of war or terrorism,
            civil unrest, national grid failure, acts of government, or major internet service provider failures.
        </p>
        <ul className="list-disc pl-5 mt-2 space-y-1">
          <li>
            The affected Party must notify the other in writing within 48 hours of becoming aware of a Force Majeure Event,
            specifying the nature and anticipated duration thereof.
          </li>
          <li>
            If a Force Majeure Event continues for more than 30 consecutive days, either Party may terminate this Agreement
            on written notice without penalty. Provider shall issue a pro-rated refund for any prepaid fees in respect of 
            Services not delivered due to the Force Majeure Event.
          </li>
        </ul>
      </section>

      {/* Section 11 */}
      <section>
        <h3 className="font-bold text-lg mb-3 text-gray-900 border-b border-gray-200 pb-2">
          11. General Provisions
        </h3>
        <div className="space-y-2 text-sm">
          <p>
            <strong>11.1 Governing Law:</strong> This Agreement shall be governed by and construed in accordance with the laws
            of the Federal Republic of Nigeria. Each Party irrevocably submits to the non-exclusive jurisdiction of the courts
            of Nigeria in respect of any dispute arising under or in connection with this Agreement.
          </p>
          <p>
            <strong>11.2 Entire Agreement:</strong> This Agreement, together with all Exhibits, Subscription Orders, and SOWs, 
            constitutes the entire agreement between the Parties with respect to its subject matter and supersedes all prior and
            contemporaneous proposals, representations, and understandings, whether written or oral.
          </p>
          <p>
            <strong>11.3 Order of Precedence:</strong> In the event of any conflict between documents, the order of precedence shall be:
            (1) Subscription Order or SOW; (2) this MSA; (3) the SLA; (4) the DPA.
          </p>
          <p>
            <strong>11.4 Assignment:</strong> Neither Party may assign or transfer this Agreement without the prior written consent of 
            the other Party, except that Provider may assign this Agreement in connection with a merger, acquisition, or sale of all or 
            substantially all of its assets.
          </p>
          <p>
            <strong>11.5 Notices:</strong> All notices shall be in writing and delivered by hand, overnight courier, or email with 
            confirmed read receipt, to the addresses set forth in the applicable Subscription Order.

          </p>
          <p>
            <strong>11.6 Severability:</strong> If any provision of this Agreement is held to be invalid or unenforceable, 
            such provision shall be modified to the minimum extent necessary to make it enforceable, and the remaining 
            provisions shall continue in full force.
          </p>
          <p>
            <strong>11.7 Waiver:</strong> No failure or delay by either Party in exercising any right under this
            Agreement shall constitute a waiver of that right.
          </p>
          <p>
            <strong>11.8  Counterparts:</strong> This Agreement may be executed in counterparts;
            electronic signatures shall be deemed valid and binding to the same extent as original signatures.
          </p>
          <p>
            <strong>11.9  Survival:</strong> Sections 4.1, 5, 6, 8, 9, 12.3, 12.6, 12.8, and 11 shall
             survive expiry or termination of this Agreement for any reason.
          </p>
          <p>
            <strong>11.10  Amendments:</strong> No amendment or modification of this Agreement
            shall be effective unless made in writing and signed by authorised representatives of both Parties.
          </p>
        </div>
      </section>

      {/* Section 12 */}
      <section>
        <h3 className="font-bold text-lg mb-3 text-gray-900 border-b border-gray-200 pb-2">
          12. Professional Services & Security Engagements
        </h3>
        <div className="space-y-2 text-sm">
          <p>
            This Section 12 governs all Professional Services and Security Engagements offered by Provider as optional 
            add-ons to the standard subscription. In the event of any conflict between this Section and the main body of the MSA,
             the provisions of this Section shall prevail in respect of the subject matter hereof.
          </p>
          <p>
            <strong>12.1 Scope and Engagement:</strong> The following Professional Services and Security Engagements are available to 
            Customer as optional add-ons, as specified in the applicable Subscription Order or Statement of Work:
          </p>

          <div className="overflow-x-auto mt-2">
            <table className="min-w-full text-left text-xs border">
              <thead className="bg-gray-100 font-bold">
                <tr>
                  <th className="p-2 border">Engagement</th>
                  <th className="p-2 border">Fee Structure</th>
                  <th className="p-2 border">Availability</th>
                  <th className="p-2 border">Validity</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="p-2 border font-semibold">
                    Security Assurance Pack
                  </td>
                  <td className="p-2 border">
                    ₦500,000 one-off
                  </td>
                  <td className="p-2 border">
                    All products
                  </td>
                  <td className="p-2 border">
                    12 months from issue date
                  </td>
                </tr>
                <tr>
                  <td className="p-2 border font-semibold">
                    Bring Your Own Key (BYOK) Encryption
                  </td>
                  <td className="p-2 border">
                    ₦1,500,000 setup + ₦300,000/mo
                  </td>
                  <td className="p-2 border">
                    25th Direct & AI Recruiter
                  </td>
                  <td className="p-2 border">
                    Ongoing — subscription-based
                  </td>
                </tr>
                <tr>
                  <td className="p-2 border font-semibold">
                    Advanced Audit & Compliance Reporting
                  </td>
                  <td className="p-2 border">
                    ₦200,000/month
                  </td>
                  <td className="p-2 border">
                    All products
                  </td>
                  <td className="p-2 border">
                    Ongoing — subscription-based
                  </td>
                </tr>
                <tr>
                  <td className="p-2 border font-semibold">
                    Dedicated Security Review (Annual)
                  </td>
                  <td className="p-2 border">
                    ₦2,500,000/year
                  </td>
                  <td className="p-2 border">
                    Direct & AI Recruiter (Enterprise)
                  </td>
                  <td className="p-2 border">
                    12 months from review date
                  </td>
                </tr>
                
              </tbody>
            </table>
          </div>

          <p className="mt-3">
            Each engagement shall be activated by a separate Subscription Order or SOW. All fees for Professional 
            Services and Security Engagements are non-refundable once the engagement has commenced.
          </p>
          <p>
            <strong>12.2 Security Assurance Pack:</strong> The Security Assurance Pack is a one-time engagement providing Customer with: 
            (a) a shared penetration test summary report; (b) completion of Customer's standard enterprise security questionnaire; 
            (c) a DPA review call of up to 2 hours; and (d) a sub-processor disclosure document. All outputs are valid for 12 months 
            from the date of issue and do not constitute legal or regulatory compliance advice.
          </p>
          <p>
            <strong>12.3 Bring Your Own Key (BYOK) Encryption:</strong> Where Customer activates BYOK Encryption, 
            Customer shall manage its own encryption keys via AWS Key Management Service (KMS) or an equivalent 
            system agreed with Provider. The following terms apply:
             
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>
                Customer is solely and exclusively responsible for the generation, storage, rotation, and revocation of its encryption keys
              </li>
              <li>
                Provider shall have no ability to access, recover, or reconstruct Customer Data if Customer's encryption keys are lost, 
                deleted, revoked, or otherwise rendered unavailable
              </li>
              <li>
                In the event of key loss or deletion, Provider's obligation to make Customer Data available for export under Section 3.3 shall 
                be suspended and Provider shall bear no liability for the resulting inaccessibility of Customer Data
              </li>
              <li>
              	Customer shall maintain adequate backup and recovery procedures for its encryption keys
              </li>
            </ul>
          </p>
          <p>
            <strong>Critical Notice:</strong> Loss of encryption keys under BYOK results in permanent and irrecoverable 
            loss of Customer Data. This risk is entirely assumed by Customer upon activation. Provider strongly recommends
             Customer consult its IT and legal teams before activating BYOK.
          </p>
          <p>
            <strong>12.4 Advanced Audit & Compliance Reporting:</strong> Advanced Audit & Compliance Reporting provides Customer 
            with enhanced audit logs, compliance dashboards, and exportable reports on a monthly basis for internal use. Reports 
            are informational only and do not constitute legal advice, regulatory compliance certification, or an opinion on 
            Customer's compliance with any applicable law or regulation. Provider does not warrant the completeness or accuracy of 
            reports for regulatory reliance purposes.
          </p>
          <p>
            <strong>12.5 Dedicated Security Review (Annual):</strong> The Dedicated Security Review is available to Enterprise tier 
            customers and comprises: (a) a dedicated security review session of up to 4 hours; (b) an updated penetration test summary; 
            (c) a bespoke security questionnaire response; and (d) a written summary of material security changes in the preceding 12 months.
          </p>
          <p>
            <strong>No-Guarantee Disclaimer:</strong> The Dedicated Security Review does not guarantee that the platform is free from vulnerabilities, 
            security incidents, or data breaches. The review reflects Provider's security posture at the time of the review and is valid for 12 months 
            from the review date.
          </p>
          <p>
            All outputs of the Dedicated Security Review remain the property of Provider. Customer receives a non-exclusive, 
            non-transferable licence to use the outputs for its internal purposes only. Customer shall not publish, distribute, 
            or share review outputs with third parties without Provider's prior written consent.
          </p>
          <p>
            <strong>12.6 Output Ownership and License:</strong> All deliverables, reports, documents, and outputs produced by Provider 
            in connection with any Professional Service or Security Engagement ("Engagement Outputs") remain the intellectual property of Provider. 
            Provider grants Customer a limited, non-exclusive, non-transferable licence to use Engagement Outputs solely for Customer's internal 
            business purposes during the term of the applicable engagement. Customer shall not publish, distribute, or use Engagement Outputs to 
            support claims or proceedings against Provider.
          </p>
          <p>
            <strong>12.7 Fees, Payment and Cancellation:</strong> All Professional Services and Security Engagement fees are subject to the payment 
            terms in Section 2.3. In addition:
             
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>
                Security Assurance Pack fees are payable in full upon execution of the Subscription Order. 
                Engagement commences within 10 business days of payment
              </li>
              <li>
              	BYOK and Advanced Audit & Compliance Reporting fees are billed annually or 
                quarterly in line with Customer's parent subscription billing cycle
              </li>
              <li>
              	Dedicated Security Review fees are billed annually in advance. The review must be 
                scheduled within 12 months of payment or the fee is forfeited
              </li>
              <li>
              	All Professional Services fees are non-refundable once commenced. Customer may cancel an 
                uncommenced engagement with 14 days' written notice for a full refund
              </li>
            </ul>
          </p>
          <p>
            <strong>12.8 Limitation of Liability for Security Engagements:</strong> Provider's aggregate liability in connection with any 
            Professional Service or Security Engagement shall not exceed the total fees paid by Customer for that specific engagement in the 
            12 months preceding the relevant claim. Provider shall not be liable for: (a) regulatory fines arising from Customer's reliance on 
            Engagement Outputs; (b) security incidents occurring after the date of a Security Assurance Pack or Dedicated Security Review; 
            (c) loss arising from Customer's failure to implement recommendations in any Engagement Output; or (d) irrecoverable data loss arising 
            from Customer's management of BYOK encryption keys. The foregoing limitations do not apply to liability arising from Provider's wilful 
            misconduct or fraud.
          </p>
          <p>
            <strong>12.9 Survival:</strong> Sections 12.3 (BYOK key loss disclaimer), 12.6 (Output Ownership), 
            and 12.8 (Limitation of Liability for Security Engagements) shall survive termination or expiry of this Agreement for any reason.
          </p>
        </div>
      </section>

      {/* Exhibits */}
      <section>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 space-y-3 text-sm">
          <h3 className="font-bold text-base text-gray-800">
            Master Service Documentation Pack
          </h3>
          <div className="mb-6">
            <h4 className="font-semibold mb-1">
              Exhibit A: Master Services Agreement (MSA)
            </h4>
            <p>
              Full version will be provided to Customer via email upon execution
              of this Agreement.
            </p>
          </div>

          {/* Exhibit B */}
          <div className="mb-6">
            <h4 className="font-semibold mb-1">
              Exhibit B: Service Level Agreement (SLA)
            </h4>
            <p>
              Full version will be provided to Customer via email upon execution
              of this Agreement.
            </p>
          </div>

          {/* Exhibit C */}
          <div>
            <h4 className="font-semibold mb-1">
              Exhibit C: Data Processing Addendum (DPA)
            </h4>
            <p>
              Full version will be provided to Customer via email upon execution
              of this Agreement.
            </p>
          </div>
        </div>
      </section>

      {/* Signatures */}
      <div className="mt-10 border-t pt-8">
        <h3 className="font-bold text-xl mb-4">Execution</h3>
        <p className="text-sm text-gray-600 mb-6">
          This Agreement is executed by the undersigned, who represent that they
          are duly authorized to bind their respective Party.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          {/* Provider side */}
          <div className="border p-6 rounded-lg bg-gray-50">
            <p className="font-bold mb-1">For and on behalf of the Provider:</p>
            <p className="text-primary font-semibold mb-4">
              25th &amp; Staffing
            </p>
            <div className="space-y-3">
              <img src={img.ceoSign} alt="CEO Sign" className="h-16" />
              <p className="border-b border-gray-400 pb-1">
                <strong>Name:</strong> Emem Usen
              </p>
              <p className="border-b border-gray-400 pb-1">
                <strong>Title:</strong> CEO
              </p>
              <p className="border-b border-gray-400 pb-1">
                <strong>Date:</strong> {date}
              </p>
            </div>
          </div>

          {/* Customer side */}
          <div className="border p-6 rounded-lg bg-gray-50">
            <p className="font-bold mb-1">For and on behalf of the Customer:</p>
            <p className="text-primary font-semibold mb-4">{clientName}</p>
            <div className="space-y-3">
              <div className="h-16 w-full">
                {readOnly ? (
                  <div className="h-16 flex items-end">
                    <span className="border-b border-gray-400 block w-48 text-gray-400 text-xs pb-1">
                      Signature
                    </span>
                  </div>
                ) : !signaturePreview ? (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Upload Signature
                    </label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/jpg,image/png"
                      onChange={handleFileChange}
                      className="block w-full text-sm text-gray-500
                        file:mr-4 file:py-2 file:px-4
                        file:rounded-md file:border-0
                        file:text-sm file:font-semibold
                        file:bg-primary file:text-white
                        hover:file:bg-primary/90
                        cursor-pointer"
                    />
                  </div>
                ) : (
                  <div
                    className="relative group cursor-pointer"
                    onClick={handleImageClick}
                  >
                    <img
                      src={signaturePreview}
                      alt="Uploaded Signature"
                      className="h-16 object-contain"
                    />
                    <button
                      onClick={handleRemoveClick}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1
                        opacity-0 group-hover:opacity-100 transition-opacity duration-200
                        hover:bg-red-600"
                      aria-label="Remove signature"
                    >
                      <X size={16} />
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/jpg,image/png"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </div>
                )}
              </div>
              <p className="border-b border-gray-400 pb-1">
                <strong>Name:</strong> {representativeName}
              </p>
              <p className="border-b border-gray-400 pb-1">
                <strong>Title:</strong>{" "}
                {representativeRole ||
                  "Managing Director / Chief Executive Officer"}
              </p>
              <p className="border-b border-gray-400 pb-1">
                <strong>Date:</strong> {date}
              </p>
            </div>
          </div>
        </div>

        {/* Optional blocks */}
        <div className="mt-8 bg-gray-50 border border-gray-200 rounded-lg p-5 space-y-3 text-sm text-gray-600">
          <p className="font-semibold text-gray-700">OPTIONAL</p>
          <p>
            <strong>Duly Authorized by Board Resolution</strong> {date} (if
            applicable)
          </p>
          <p>
            <strong>Witnessed by (optional):</strong>
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <p className="border-b border-gray-300 pb-1">Name: ____________</p>
            <p className="border-b border-gray-300 pb-1">
              Title: Company Secretary / Head of Legal
            </p>
            <p className="border-b border-gray-300 pb-1">Date: {date}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MSAContent;
